import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Logo from './Logo';
import CartNav from './CartNav';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { IDS } from '@/constants/testIds';
import { Menu, X, LogOut, ShoppingBag } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Home', id: IDS.header.navHome },
  { to: '/products', label: 'Catalog', id: IDS.header.navCatalog },
  { to: '/about', label: 'About', id: IDS.header.navAbout },
  { to: '/contact', label: 'Contact', id: IDS.header.navContact },
];

function Header() {
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header
      style={{ background: 'var(--ije-black)', color: 'var(--ije-white)' }}
      className="border-b border-neutral-800 sticky top-0 z-50"
    >
      <div className="container-x flex items-center justify-between py-5">
        <Link to="/" className="flex items-center">
          <Logo size="small" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={n.id}
              className={({ isActive }) =>
                `sans text-[0.72rem] tracking-[0.28em] uppercase transition-colors ${
                  isActive ? 'text-white' : 'text-neutral-400 hover:text-white'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          {user && user.role !== 'admin' && <CartNav />}
          {user === null && (
            <>
              <NavLink
                to="/login"
                data-testid={IDS.header.navLogin}
                className="sans text-[0.72rem] tracking-[0.28em] uppercase text-neutral-400 hover:text-white transition-colors"
              >
                Sign In
              </NavLink>
              <Link to="/apply" data-testid={IDS.header.navApply} className="btn-primary" style={{ padding: '0.6rem 1.2rem' }}>
                Apply
              </Link>
            </>
          )}
          {user && user.role === 'admin' && (
            <NavLink
              to="/admin"
              data-testid={IDS.header.navAdmin}
              className={({ isActive }) =>
                `sans text-[0.72rem] tracking-[0.28em] uppercase transition-colors ${
                  isActive ? 'text-white' : 'text-neutral-400 hover:text-white'
                }`
              }
            >
              Admin
            </NavLink>
          )}
          {user && user.role !== 'admin' && (
            <NavLink
              to="/dashboard"
              data-testid={IDS.header.navDashboard}
              className={({ isActive }) =>
                `sans text-[0.72rem] tracking-[0.28em] uppercase transition-colors ${
                  isActive ? 'text-white' : 'text-neutral-400 hover:text-white'
                }`
              }
            >
              Dashboard
            </NavLink>
          )}
          {user && (
            <button
              type="button"
              onClick={handleLogout}
              data-testid={IDS.header.navLogout}
              className="sans text-[0.72rem] tracking-[0.28em] uppercase text-neutral-400 hover:text-white transition-colors flex items-center gap-2"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Sign Out
            </button>
          )}
        </div>

        <button
          type="button"
          className="md:hidden text-white flex items-center gap-3"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {user && user.role !== 'admin' && cartCount > 0 && !open && (
            <span
              className="sans text-[0.6rem] font-semibold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center inline-flex items-center gap-1"
              style={{ background: 'linear-gradient(135deg, #F2A6CE, #C2478A)', color: '#fff' }}
              aria-label={`${cartCount} items in cart`}
            >
              <ShoppingBag size={11} strokeWidth={2} />
              {cartCount > 99 ? '99+' : cartCount}
            </span>
          )}
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-800 bg-black">
          <div className="container-x py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`m-${n.id}`}
                className="sans text-[0.75rem] tracking-[0.28em] uppercase text-neutral-300"
              >
                {n.label}
              </NavLink>
            ))}
            {user === null && (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className="sans text-[0.75rem] tracking-[0.28em] uppercase text-neutral-300">
                  Sign In
                </NavLink>
                <Link to="/apply" onClick={() => setOpen(false)} className="btn-primary self-start">Apply for Account</Link>
              </>
            )}
            {user && user.role === 'admin' && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="sans text-[0.75rem] tracking-[0.28em] uppercase text-neutral-300">Admin</NavLink>
            )}
            {user && user.role !== 'admin' && (
              <>
                <NavLink to="/dashboard" onClick={() => setOpen(false)} className="sans text-[0.75rem] tracking-[0.28em] uppercase text-neutral-300">Dashboard</NavLink>
                <NavLink to="/cart" onClick={() => setOpen(false)} className="sans text-[0.75rem] tracking-[0.28em] uppercase text-neutral-300 flex items-center gap-3" data-testid="m-nav-cart">
                  <ShoppingBag size={14} strokeWidth={1.5} />
                  Cart
                  {cartCount > 0 && (
                    <span
                      className="sans text-[0.6rem] tracking-normal font-semibold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center"
                      style={{ background: 'linear-gradient(135deg, #F2A6CE, #C2478A)', color: '#fff' }}
                    >
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </NavLink>
              </>
            )}
            {user && (
              <button
                type="button"
                onClick={() => { setOpen(false); handleLogout(); }}
                className="sans text-[0.75rem] tracking-[0.28em] uppercase text-neutral-300 text-left"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer style={{ background: 'var(--ije-black)', color: 'var(--ije-white)' }} className="mt-24">
      <div className="container-x py-16 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo size="small" />
          <p className="sans text-sm text-neutral-400 mt-6 max-w-md leading-relaxed">
            A boutique wholesale storefront for approved retail partners. A growing house of curated
            intimate-product labels across every category.
          </p>
        </div>
        <div>
          <h4 className="eyebrow mb-4" style={{ color: '#fff' }}>Shop</h4>
          <ul className="space-y-3">
            <li><Link to="/products" className="sans text-sm text-neutral-400 hover:text-white">Catalog</Link></li>
            <li><Link to="/apply" className="sans text-sm text-neutral-400 hover:text-white">Apply for Account</Link></li>
            <li><Link to="/login" className="sans text-sm text-neutral-400 hover:text-white">Sign In</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="eyebrow mb-4" style={{ color: '#fff' }}>Company</h4>
          <ul className="space-y-3">
            <li><Link to="/about" className="sans text-sm text-neutral-400 hover:text-white">About</Link></li>
            <li><Link to="/contact" className="sans text-sm text-neutral-400 hover:text-white">Contact</Link></li>
          </ul>
        </div>
      </div>
      <div className="hair hair--dark" />
      <div className="container-x py-6 flex flex-col md:flex-row justify-between gap-3">
        <p className="sans text-xs text-neutral-500 tracking-[0.2em] uppercase">© {new Date().getFullYear()} IJE HAUS LLC</p>
        <p className="sans text-xs text-neutral-500 tracking-[0.2em] uppercase">Wholesale &middot; Trade Only &middot; 21+</p>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-full flex flex-col" style={{ background: 'var(--ije-white)' }}>
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
